<template>
  <div>
    <div class="d-flex mb-3">
      <button class="btn btn-green d-flex align-items-center" @click="openCreateProduct">
        <mdicon name="plus" class="me-2" />
        Create product
      </button>
    </div>

    <EasyDataTable
      ref="dataTable"
      v-model:server-options="serverOptions"
      table-class-name="vue3-easy-data-table table-shadow no-overflow"
      :headers="headers"
      :items="products"
      :server-items-length="serverItemsLength"
      :loading="loading"
      hide-footer
      no-hover
      alternating
      @update:server-options="updateServerOptions"
    >
      <template #item="{ column, item }">
        <span v-if="column === 'createdAt'">{{ readableDate(item.createdAt) }}</span>
        <span v-else-if="column === 'onHand'">
          {{ item.onHand }}
          <span v-if="item.onHand < 10" class="badge bg-danger ms-2">Low stock</span>
        </span>
        <span v-else>{{ item[column] }}</span>
      </template>
      <template #no-data>-</template>
    </EasyDataTable>

    <TablePagination
      v-model:server-options-ref="serverOptions"
      v-model:data-table-ref="dataTable"
      :server-items-length="serverItemsLength"
    />

    <CreateProductModal :id="CREATE_INVENTORY_PRODUCT" :company-id="companyId" @created="handleProductCreated" />
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, ref, toRef } from 'vue'
import { useInventoryServerPagination } from '@/composables/useInventoryServerPagination'
import { CREATE_INVENTORY_PRODUCT } from '@/const/ModalTypes'
import { readableDate } from '@/services/Helpers'
import { openModal } from '@/services/Modal'
import { useInventoryStore } from '@/store/inventory'
import EasyDataTable, { type Header } from 'vue3-easy-data-table'
import TablePagination from '@/views/Components/Elements/Table/TablePagination.vue'
import CreateProductModal from '@/views/Modals/Inventory/CreateProductModal.vue'

export default defineComponent({
  name: 'InventoryProductsPage',
  components: { EasyDataTable, TablePagination, CreateProductModal },
  props: {
    companyId: {
      type: String,
      required: true,
    },
  },
  setup(props) {
    const inventoryStore = useInventoryStore()
    const products = computed(() => inventoryStore.productsList)
    const loading = computed(() => inventoryStore.productsLoading)
    const serverItemsLength = computed(() => inventoryStore.productsCount)
    const dataTable = ref()
    const companyId = toRef(props, 'companyId')

    const headers: Header[] = [
      { text: 'SKU', value: 'sku' },
      { text: 'Name', value: 'name' },
      { text: 'On hand', value: 'onHand' },
      { text: 'Created', value: 'createdAt' },
    ]

    const { serverOptions, updateServerOptions, resetAndReload } = useInventoryServerPagination(
      companyId,
      (options) => inventoryStore.fetchProducts(companyId.value, { page: options.page, rowsPerPage: options.rowsPerPage }),
    )

    const openCreateProduct = () => openModal(CREATE_INVENTORY_PRODUCT)

    const handleProductCreated = () => {
      resetAndReload()
    }

    return {
      dataTable,
      products,
      loading,
      serverItemsLength,
      headers,
      serverOptions,
      updateServerOptions,
      openCreateProduct,
      handleProductCreated,
      readableDate,
      CREATE_INVENTORY_PRODUCT,
    }
  },
})
</script>
